/**
 * NovaExportacaoForm (M9, RF-M9-006 AC1) — formulário "Nova exportação":
 * escopo (tudo/filtro/escola), formato (csv/jsonl), "incluir valores lidos pela IA".
 *
 * Recebe `escopoInicial`/`filtroInicial` dos atalhos ("Exportar visitas desta
 * escola" → escola; "Exportar este resultado" → filtro). Ao enviar, chama a
 * mutação de criação e exibe um aviso "na fila". Nenhum dado pessoal em console
 * (RN-M11-001).
 * ADR-038: o filtro Evidência (com "Incluir referências") no formulário — vem marcado com o do atalho e vale em
 * qualquer escopo; na base inteira, escolher uma evidência torna a exportação filtrada (só aquele tipo).
 * "Resultado filtrado" e "Escola selecionada" só existem vindo do atalho da tela de origem (o filtro vem junto);
 * indisponíveis, um texto diz onde estão os atalhos (relato do usuário de 08/10/2026 — ajuste só de texto).
 */

import { useState } from 'react';
import { useCriarExportacao } from '../hooks/useExportacoes';
import {
  ExportacaoEscopo,
  ExportacaoFiltro,
  ExportacaoFormato,
} from '../services/exportacoes-types';
import { FiltroEvidencia, ValorFiltroEvidencia } from './Evidencia';

/** O filtro a enviar: o do escopo (sem a evidência antiga) mais a evidência escolhida no formulário. */
function filtroComEvidencia(base: ExportacaoFiltro | undefined, evid: ValorFiltroEvidencia): ExportacaoFiltro {
  const { evidencia: _e, incluirReferencias: _r, ...resto } = base ?? {};
  return {
    ...resto,
    ...(evid.evidencia ? { evidencia: evid.evidencia } : {}),
    ...(evid.evidencia === 'planilha' && evid.incluirReferencias ? { incluirReferencias: '1' as const } : {}),
  };
}

export interface NovaExportacaoFormProps {
  readonly escopoInicial?: ExportacaoEscopo;
  readonly filtroInicial?: ExportacaoFiltro;
  onCriada?(): void;
}

/** Onde estão os atalhos dos escopos indisponíveis (nulo quando todos estão disponíveis). */
function ajudaDoEscopo(filtroInicial: ExportacaoFiltro | undefined): string | null {
  if (!filtroInicial) {
    return (
      '“Resultado filtrado” e “Escola selecionada” vêm dos atalhos das outras telas: “Exportar este resultado” na tela ' +
      'de Visitas (com os filtros aplicados) e “Exportar visitas desta escola” na ficha da escola. Para uma linha por ' +
      'escola, com a última visita, use “Exportar CSV” na tela de Escolas.'
    );
  }
  if (!filtroInicial.escolaInep) {
    return '“Escola selecionada” vem do atalho “Exportar visitas desta escola” na ficha da escola.';
  }
  return null;
}

export function NovaExportacaoForm({
  escopoInicial = 'tudo',
  filtroInicial,
  onCriada,
}: NovaExportacaoFormProps): JSX.Element {
  const { criar, enviando, erro } = useCriarExportacao();
  const [escopo, setEscopo] = useState<ExportacaoEscopo>(escopoInicial);
  const [formato, setFormato] = useState<ExportacaoFormato>('csv');
  const [incluirLidos, setIncluirLidos] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const ajuda = ajudaDoEscopo(filtroInicial);
  const [evidencia, setEvidencia] = useState<ValorFiltroEvidencia>({
    evidencia: filtroInicial?.evidencia,
    incluirReferencias: filtroInicial?.incluirReferencias === '1' || undefined,
  });

  async function enviar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setAviso(null);
    const filtro = filtroComEvidencia(escopo === 'tudo' ? undefined : filtroInicial, evidencia);
    // Base inteira com uma evidência escolhida = exportação filtrada só por ela.
    const escopoFinal: ExportacaoEscopo = escopo === 'tudo' && Object.keys(filtro).length > 0 ? 'filtro' : escopo;
    await criar({
      formato,
      escopo: escopoFinal,
      filtro: escopoFinal === 'tudo' ? undefined : filtro,
      incluirLidos,
    });
    setAviso('Exportação na fila — acompanhe o status na tabela abaixo.');
    onCriada?.();
  }

  return (
    <section className="card">
      <h2>Nova exportação</h2>
      <form onSubmit={enviar}>
        <div className="form-field">
          <label htmlFor="escopo-exp">Escopo</label>
          <select
            id="escopo-exp"
            className="input"
            value={escopo}
            aria-describedby={ajuda ? 'escopo-exp-ajuda' : undefined}
            onChange={(e) => setEscopo(e.target.value as ExportacaoEscopo)}
          >
            <option value="tudo">Base inteira</option>
            <option value="filtro" disabled={!filtroInicial}>
              Resultado filtrado
            </option>
            <option value="escola" disabled={!filtroInicial?.escolaInep}>
              Escola selecionada
            </option>
          </select>
          {ajuda && (
            <p className="muted" id="escopo-exp-ajuda">
              {ajuda}
            </p>
          )}
        </div>

        <FiltroEvidencia id="evidencia-exp" valor={evidencia} onMudar={setEvidencia} />

        <div className="form-field">
          <label htmlFor="formato-exp">Formato</label>
          <select
            id="formato-exp"
            className="input"
            value={formato}
            onChange={(e) => setFormato(e.target.value as ExportacaoFormato)}
          >
            <option value="csv">CSV (Excel)</option>
            <option value="jsonl">JSON Lines</option>
          </select>
        </div>

        <div className="form-field">
          <label>
            <input
              type="checkbox"
              checked={incluirLidos}
              onChange={(e) => setIncluirLidos(e.target.checked)}
            />{' '}
            Incluir valores lidos pela IA
          </label>
        </div>

        <button type="submit" className="btn btn-primary" disabled={enviando}>
          {enviando ? 'Enviando…' : 'Gerar exportação'}
        </button>

        {aviso && <p className="notice notice-ok">{aviso}</p>}
        {erro && <p className="notice notice-info">Não foi possível solicitar a exportação. Tente novamente.</p>}
      </form>
    </section>
  );
}
